import type { InboxContactFragment } from "@qw/graphql";
import { useContacts, useEmailDraft } from "@qw/ui";
import { type Editor, mergeAttributes, Node, type Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import {
  NodeViewWrapper,
  type ReactNodeViewProps,
  ReactNodeViewRenderer,
  ReactRenderer,
  useCurrentEditor,
} from "@tiptap/react";
import SuggestionPlugin, { type SuggestionKeyDownProps, type SuggestionProps } from "@tiptap/suggestion";
import {
  forwardRef,
  type MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import tippy, { type Instance } from "tippy.js";

// Unique plugin key to avoid conflicts with other suggestion plugins (e.g., EmojiSuggestion)
const contactMentionPluginKey = new PluginKey("contactMention");

// ============================================================================
// Types
// ============================================================================

interface ContactListProps {
  query: string;
  command: (item: InboxContactFragment) => void;
}

interface ContactListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

// ============================================================================
// Contact Mention Node Component (for rendering in editor)
// ============================================================================

const ContactMentionNodeComponent = (props: ReactNodeViewProps<HTMLSpanElement>) => {
  const { editor } = useCurrentEditor();
  const { node, getPos } = props;

  const selectNode = () => editor?.commands.setNodeSelection(getPos() ?? 0);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    selectNode();
  };

  return (
    <NodeViewWrapper as="span" onClick={handleClick} className="contact-mention inline">
      <a
        href={`mailto:${node.attrs.email}`}
        contentEditable={false}
        className="in-[.ProseMirror-selectednode]:ring-2 in-[.ProseMirror-selectednode]:ring-primary/50 in-[.ProseMirror-selectednode]:rounded-sm selection:bg-transparent!"
      >
        @{node.attrs.label}
      </a>
    </NodeViewWrapper>
  );
};

// ============================================================================
// Contact Mention Node Extension
// ============================================================================

export const ContactMentionNode = Node.create({
  name: "contactMention",
  group: "inline",
  inline: true,
  atom: true,

  addAttributes() {
    return {
      email: {
        default: "",
      },
      label: {
        default: "",
      },
    };
  },

  parseHTML() {
    return [
      {
        priority: 100,
        tag: 'a[data-type="contact-mention"]',
        getAttrs(node) {
          if (typeof node === "string") {
            return false;
          }
          const email = node.getAttribute("href")?.replace("mailto:", "") || "";
          const label = node.textContent?.replace(/^@/, "") || "";
          return { email, label };
        },
      },
    ];
  },

  renderHTML({ node }) {
    return [
      "a",
      mergeAttributes({
        "data-type": "contact-mention",
        href: `mailto:${node.attrs.email}`,
      }),
      `@${node.attrs.label}`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ContactMentionNodeComponent);
  },
});

// ============================================================================
// ContactList Component (for suggestion popup)
// ============================================================================

const ContactList = forwardRef<ContactListRef, ContactListProps>(({ query, command }, ref) => {
  const { contacts } = useContacts();
  const { handleUpdateRecipientsTo } = useEmailDraft();
  const { getValues, setValue } = useFormContext();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // The suggestion popup renders through the editor's portal, so it sits inside the draft's form and
  // can add the mentioned contact to To directly.
  const selectContact = useCallback(
    (contact: InboxContactFragment) => {
      command(contact);

      const currentTo: string[] = getValues("to") || [];

      if (!currentTo.includes(contact.email)) {
        const newTo = [...currentTo, contact.email];
        setValue("to", newTo);
        handleUpdateRecipientsTo(newTo);
      }
    },
    [command, getValues, setValue, handleUpdateRecipientsTo]
  );

  // Filter contacts based on query
  const filteredContacts = useMemo(() => {
    const lowerQuery = query.toLowerCase();
    return contacts
      .filter(
        (contact) =>
          contact.displayName?.toLowerCase().includes(lowerQuery) || contact.email.toLowerCase().includes(lowerQuery)
      )
      .slice(0, 10);
  }, [contacts, query]);

  // Reset selection when filtered contacts change
  useEffect(() => setSelectedIndex(0), [filteredContacts]);

  // Scroll selected item into view
  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }) => {
        if (filteredContacts.length === 0) {
          return false;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setSelectedIndex((prev) => (prev + filteredContacts.length - 1) % filteredContacts.length);
          return true;
        }
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % filteredContacts.length);
          return true;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          // Clamp selectedIndex to valid bounds to handle race condition when contacts array shrinks
          const safeIndex = Math.min(selectedIndex, filteredContacts.length - 1);
          const item = filteredContacts[safeIndex];
          if (item) {
            selectContact(item);
          }
          return true;
        }
        return false;
      },
    }),
    [filteredContacts, selectedIndex, selectContact]
  );

  return (
    <div className="bg-popover rounded-md shadow-md border p-1 max-h-64 overflow-y-auto">
      {filteredContacts.length > 0 ? (
        filteredContacts.map((item, index) => (
          <button
            key={item.email}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            onClick={() => selectContact(item)}
            className={`relative flex flex-col gap-0.5 w-full cursor-default select-none items-start rounded-sm pl-2 pr-3 py-1.5 text-xs outline-hidden transition-colors ${
              index === selectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
            }`}
          >
            <span className="font-medium">{item.displayName || item.email}</span>
            <span className="text-muted-foreground">{item.email}</span>
          </button>
        ))
      ) : (
        <div className="px-2 py-1.5 text-xs text-muted-foreground">No contacts found</div>
      )}
    </div>
  );
});

ContactList.displayName = "ContactList";

// ============================================================================
// Suggestion Helpers
// ============================================================================

const getClientRect = (clientRect: (() => DOMRect | null) | null | undefined): (() => DOMRect) => {
  return () => clientRect?.() ?? new DOMRect(0, 0, 0, 0);
};

const suggestionConfig = {
  char: "@",

  // Return empty array - ContactList handles fetching and filtering via useContacts
  items: () => [],

  render: () => {
    let component: ReactRenderer<ContactListRef, ContactListProps>;
    let popup: Instance[];

    return {
      onStart: (props: SuggestionProps<InboxContactFragment>) => {
        component = new ReactRenderer(ContactList, {
          props: { query: props.query, command: props.command },
          editor: props.editor,
        });

        popup = tippy("body", {
          getReferenceClientRect: getClientRect(props.clientRect),
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },

      onUpdate: (props: SuggestionProps<InboxContactFragment>) => {
        component.updateProps({ query: props.query, command: props.command });
        popup[0].setProps({ getReferenceClientRect: getClientRect(props.clientRect) });
      },

      onKeyDown: (props: SuggestionKeyDownProps) => component.ref?.onKeyDown(props) ?? false,

      onExit: () => {
        popup[0].destroy();
        component.destroy();
      },
    };
  },

  command: ({ editor, range, props }: { editor: Editor; range: Range; props: InboxContactFragment }) => {
    const label = props.displayName || props.email;

    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent([
        { type: "contactMention", attrs: { email: props.email, label } },
        { type: "text", text: " " },
      ])
      .run();
  },
};

// ============================================================================
// Extension
// ============================================================================

export const EditorExtensionContactMention = ContactMentionNode.extend({
  addProseMirrorPlugins() {
    return [
      SuggestionPlugin({
        editor: this.editor,
        pluginKey: contactMentionPluginKey,
        ...suggestionConfig,
      }),
    ];
  },
});
